import React, { useCallback, useEffect, useMemo, useState } from "react";
import MasterShell from "../common/MasterShell";
import MasterToast from "../common/MasterToast";
import { getActivityLogs, getAuditLogs, getSecurityLogs } from "../../services/logService";
import { getStationUsers } from "../../services/stationUserService";
import { getAllUsers } from "../../services/userLookupService";
import { extractApiErrorMessage, formatMasterDate } from "../../utils/masterHelpers";
import { useCapabilities } from "../../contexts/CapabilitiesContext";
import "./Logs.css";
import "./new_Logs.css";

const HIDDEN_LOG_KEYS = new Set([
  "id",
  "request_id",
  "entity_id",
  "actor_user_id",
  "user_id",
  "station_id",
  "district_id",
  "sub_division_id",
]);

const formatLogLabel = (value) =>
  String(value ?? "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const parseLogValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "object") {
    return value;
  }

  const normalizedValue = String(value).trim();

  if (!normalizedValue) {
    return null;
  }

  try {
    return JSON.parse(normalizedValue);
  } catch (_error) {
    return normalizedValue;
  }
};

const sanitizeLogValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeLogValue(item))
      .filter((item) => !isLogValueEmpty(item));
  }

  if (value && typeof value === "object") {
    return Object.entries(value).reduce((accumulator, [key, currentValue]) => {
      const normalizedKey = String(key).trim().toLowerCase();

      if (
        HIDDEN_LOG_KEYS.has(normalizedKey) ||
        (normalizedKey.endsWith("_id") && normalizedKey !== "role_id")
      ) {
        return accumulator;
      }

      const sanitizedValue = sanitizeLogValue(currentValue);

      if (isLogValueEmpty(sanitizedValue)) {
        return accumulator;
      }

      accumulator[key] = sanitizedValue;
      return accumulator;
    }, {});
  }

  return value;
};

function isLogValueEmpty(value) {
  if (value === null || value === undefined || value === "") {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === "object") {
    return Object.keys(value).length === 0;
  }

  return false;
}

const formatLogScalar = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return String(value);
};

const flattenLogEntries = (value, parentKey = "") => {
  if (Array.isArray(value)) {
    const scalarValues = value.every(
      (item) => item === null || typeof item !== "object"
    );

    if (scalarValues) {
      return parentKey
        ? [{ key: parentKey, value: value.map((item) => formatLogScalar(item)).join(", ") }]
        : [];
    }

    return value.flatMap((item, index) =>
      flattenLogEntries(item, parentKey ? `${parentKey} ${index + 1}` : `${index + 1}`)
    );
  }

  if (value && typeof value === "object") {
    return Object.entries(value).flatMap(([entryKey, entryValue]) =>
      flattenLogEntries(
        entryValue,
        parentKey ? `${parentKey} ${formatLogLabel(entryKey)}` : formatLogLabel(entryKey)
      )
    );
  }

  return parentKey ? [{ key: parentKey, value: formatLogScalar(value) }] : [];
};

const renderStructuredLogValue = (value, keyPrefix = "log") => {
  if (Array.isArray(value)) {
    const scalarValues = value.every(
      (item) => item === null || typeof item !== "object"
    );

    if (scalarValues) {
      return (
        <div className="logs-module__structured-text">
          {value.map((item) => formatLogScalar(item)).join(", ")}
        </div>
      );
    }

    return (
      <div className="logs-module__structured-group">
        {value.map((item, index) => (
          <div
            key={`${keyPrefix}-item-${index}`}
            className="logs-module__structured-card"
          >
            {renderStructuredLogValue(item, `${keyPrefix}-${index}`)}
          </div>
        ))}
      </div>
    );
  }

  if (value && typeof value === "object") {
    return (
      <div className="logs-module__structured-group">
        {Object.entries(value).map(([entryKey, entryValue]) => (
          <div
            key={`${keyPrefix}-${entryKey}`}
            className="logs-module__structured-row"
          >
            <div className="logs-module__structured-label">
              {formatLogLabel(entryKey)}
            </div>
            <div className="logs-module__structured-value">
              {renderStructuredLogValue(entryValue, `${keyPrefix}-${entryKey}`)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return <div className="logs-module__structured-text">{formatLogScalar(value)}</div>;
};

const renderLogDetails = (value) => {
  const parsedValue = parseLogValue(value);
  const sanitizedValue = sanitizeLogValue(parsedValue);

  if (isLogValueEmpty(sanitizedValue)) {
    return <div className="logs-module__structured-empty">-</div>;
  }

  return <div className="logs-module__structured">{renderStructuredLogValue(sanitizedValue)}</div>;
};

const renderAuditLogDetails = (value) => {
  const parsedValue = parseLogValue(value);
  const sanitizedValue = sanitizeLogValue(parsedValue);

  if (isLogValueEmpty(sanitizedValue)) {
    return <div className="logs-module__structured-empty">-</div>;
  }

  const flattenedEntries = flattenLogEntries(sanitizedValue);

  if (flattenedEntries.length === 0) {
    return <div className="logs-module__structured-empty">-</div>;
  }

  return (
    <div className="logs-module__structured logs-module__structured--pairs">
      {flattenedEntries.map((entry, index) => (
        <div
          key={`${entry.key}-${index}`}
          className="logs-module__structured-pair"
        >
          <span className="logs-module__structured-pair-label">
            {entry.key}:
          </span>
          <span className="logs-module__structured-pair-value">
            {entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const LOG_TYPE_CONFIG = {
  activity: {
    columns: [
      {
        key: "actor",
        label: "User",
        render: (row, lookups) => (
          <div className="logs-module__cell-main">
            {lookups.userLookup[row.actor_user_id] || row.actor_name || "-"}
          </div>
        ),
      },
      {
        key: "entity",
        label: "Entity",
        render: (row) => <div className="logs-module__cell-main">{row.entity_type || "-"}</div>,
      },
      {
        key: "activity",
        label: "Activity",
        render: (row) => (
          <>
            <div className="logs-module__cell-main">{row.activity || "-"}</div>
            <div className="logs-module__cell-sub">{row.endpoint || "-"}</div>
          </>
        ),
      },
      {
        key: "ip",
        label: "IP",
        render: (row) => row.ip || "-",
      },
      {
        key: "station",
        label: "Station Name",
        render: (row) =>
          row.station_name || row.station || row.police_station || "-",
      },
      {
        key: "meta_json",
        label: "Meta",
        render: (row) => renderAuditLogDetails(row.meta_json),
      },
      {
        key: "ts",
        label: "Timestamp",
        render: (row) => formatMasterDate(row.ts),
      },
    ],
    emptyMessage: "Activity logs will appear here when the API returns records.",
    fetcher: getActivityLogs,
    filterFields: [
      { name: "from", label: "From Date", type: "date" },
      { name: "to", label: "To Date", type: "date" },
      { name: "activity", label: "Activity", placeholder: "Enter activity name" },
      { name: "entity_type", label: "Entity Type", placeholder: "Enter entity type" },
      { name: "actor_user_id", label: "Actor User", type: "select", optionType: "users" },
    ],
    label: "Activity Logs",
    requestParams: ["page", "page_size", "from", "to", "activity", "actor_user_id", "entity_type"],
  },
  audit: {
    columns: [
      {
        key: "actor",
        label: "User",
        render: (row, lookups) => (
          <div className="logs-module__cell-main">
            {lookups.userLookup[row.actor_user_id] || row.actor_name || "-"}
          </div>
        ),
      },
      {
        key: "entity",
        label: "Entity",
        render: (row) => <div className="logs-module__cell-main">{row.entity_type || "-"}</div>,
      },
      {
        key: "action",
        label: "Action",
        render: (row) => (
          <div className="logs-module__cell-main">{row.action || "-"}</div>
        ),
      },
      {
        key: "before_json",
        label: "Before",
        render: (row) => renderAuditLogDetails(row.before_json),
      },
      {
        key: "after_json",
        label: "After",
        render: (row) => renderAuditLogDetails(row.after_json),
      },
      {
        key: "ip",
        label: "IP",
        render: (row) => row.ip || "-",
      },
      {
        key: "ts",
        label: "Timestamp",
        render: (row) => formatMasterDate(row.ts),
      },
    ],
    emptyMessage: "Audit logs will appear here when the API returns records.",
    fetcher: getAuditLogs,
    filterFields: [
      { name: "from", label: "From Date", type: "date" },
      { name: "to", label: "To Date", type: "date" },
      { name: "action", label: "Action", placeholder: "Enter action, e.g. CREATE" },
      { name: "entity_type", label: "Entity Type", placeholder: "Enter entity type" },
      { name: "actor_user_id", label: "Actor User", type: "select", optionType: "users" },
    ],
    label: "Audit Logs",
    requestParams: ["page", "page_size", "from", "to", "entity_type", "action", "actor_user_id"],
  },
  security: {
    columns: [
      {
        key: "user",
        label: "User",
        render: (row, lookups) => (
          <div className="logs-module__cell-main">
            {lookups.userLookup[row.user_id] || row.email || row.username || "-"}
          </div>
        ),
      },
      {
        key: "event",
        label: "Event",
        render: (row) => (
          <>
            <div className="logs-module__cell-main">{row.event || "-"}</div>
            <div className="logs-module__cell-sub">{row.endpoint || "-"}</div>
          </>
        ),
      },
      {
        key: "detail_json",
        label: "Details",
        render: (row) => renderLogDetails(row.detail_json),
      },
      {
        key: "ip",
        label: "IP",
        render: (row) => row.ip || "-",
      },
      {
        key: "user_agent",
        label: "User Agent",
        render: (row) => renderLogDetails(row.user_agent),
      },
      {
        key: "ts",
        label: "Timestamp",
        render: (row) => formatMasterDate(row.ts),
      },
    ],
    emptyMessage: "Security logs will appear here when the API returns records.",
    fetcher: getSecurityLogs,
    filterFields: [
      { name: "from", label: "From Date", type: "date" },
      { name: "to", label: "To Date", type: "date" },
      { name: "event", label: "Event", placeholder: "Enter event name" },
      { name: "user_id", label: "User", type: "select", optionType: "users" },
    ],
    label: "Security Logs",
    requestParams: ["page", "page_size", "from", "to", "event", "user_id"],
  },
};

const createInitialFilters = (logType) =>
  LOG_TYPE_CONFIG[logType].filterFields.reduce(
    (accumulator, field) => ({
      ...accumulator,
      [field.name]: "",
    }),
    {}
  );

const buildUserLookup = (users) =>
  users.reduce((accumulator, item) => {
    if (item?.id) {
      accumulator[item.id] =
        item.name || item.username || item.email || item.id;
    }

    return accumulator;
  }, {});

const buildUserOptions = (users) =>
  users
    .map((item) => ({
      label: item?.name
        ? `${item.name}${item.username ? ` (${item.username})` : ""}`
        : item?.username || item?.email || item?.id || "-",
      value: item?.id || "",
    }))
    .filter((item) => item.value);

const getVisibleRange = (pagination, rowCount) => {
  if (rowCount === 0) {
    return "Showing 0 to 0 of 0";
  }

  const start = (pagination.currentPage - 1) * pagination.pageSize + 1;
  const end = start + rowCount - 1;

  if (pagination.totalItems !== null) {
    return `Showing ${start} to ${end} of ${pagination.totalItems}`;
  }

  return `Showing ${start} to ${end}`;
};

export default function LogsPage() {
  const [draftFilters, setDraftFilters] = useState(createInitialFilters("activity"));
  const [error, setError] = useState("");
  const [filterError, setFilterError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [logRows, setLogRows] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    hasNext: false,
    pageSize: 10,
    totalItems: null,
    totalPages: null,
  });
  const [selectedLogType, setSelectedLogType] = useState("activity");
  const [submittedFilters, setSubmittedFilters] = useState(createInitialFilters("activity"));
  const [toast, setToast] = useState({ message: "", open: false, severity: "success" });
  const [users, setUsers] = useState([]);
  const [usersError, setUsersError] = useState("");

  const { can } = useCapabilities();
  const canReadAll = can("logs", "read_all");

  const userLookup = useMemo(() => buildUserLookup(users), [users]);
  const userOptions = useMemo(() => buildUserOptions(users), [users]);
  const logConfig = LOG_TYPE_CONFIG[selectedLogType];

  // Hide user-select filters from users who cannot query across all users.
  const effectiveFilterFields = useMemo(
    () =>
      canReadAll
        ? logConfig.filterFields
        : logConfig.filterFields.filter((f) => f.optionType !== "users"),
    [canReadAll, logConfig.filterFields]
  );

  const loadUsers = useCallback(async () => {
    try {
      const response = await getAllUsers();
      setUsers(Array.isArray(response) ? response : []);
      setUsersError("");
    } catch (apiError) {
      try {
        const fallbackResponse = await getStationUsers();
        setUsers(Array.isArray(fallbackResponse) ? fallbackResponse : []);
        setUsersError(
          "Loaded user filters from station users because the broader user lookup endpoint was unavailable."
        );
      } catch (fallbackError) {
        setUsers([]);
        setUsersError(
          extractApiErrorMessage(
            fallbackError,
            "Failed to load users for log filters."
          )
        );
      }
    }
  }, []);

  const fetchLogs = useCallback(
    async (page, size, filters, logType) => {
      setIsLoading(true);
      setError("");

      try {
        const params = Object.entries({
          ...filters,
          page,
          page_size: size,
        }).reduce((accumulator, [key, value]) => {
          if (value !== "" && value !== null && value !== undefined) {
            accumulator[key] = value;
          }

          return accumulator;
        }, {});

        const result = await LOG_TYPE_CONFIG[logType].fetcher(params);

        setLogRows(result.rows);
        setPagination({
          currentPage: result.pagination.currentPage,
          hasNext: result.pagination.hasNext,
          pageSize: result.pagination.pageSize,
          totalItems: result.pagination.totalItems,
          totalPages: result.pagination.totalPages,
        });
      } catch (apiError) {
        setLogRows([]);
        setPagination((currentPagination) => ({
          ...currentPagination,
          currentPage: page,
          hasNext: false,
          pageSize: size,
          totalItems: 0,
          totalPages: 1,
        }));
        setError(
          extractApiErrorMessage(apiError, `Failed to load ${LOG_TYPE_CONFIG[logType].label.toLowerCase()}.`)
        );
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    fetchLogs(1, pageSize, submittedFilters, selectedLogType);
  }, [fetchLogs, pageSize, selectedLogType, submittedFilters]);

  const handleDraftChange = (event) => {
    const { name, value } = event.target;

    setDraftFilters((currentFilters) => ({
      ...currentFilters,
      [name]: value,
    }));
    setFilterError("");
  };

  const handleApplyFilters = (event) => {
    event.preventDefault();

    if (
      draftFilters.from &&
      draftFilters.to &&
      new Date(draftFilters.from) > new Date(draftFilters.to)
    ) {
      setFilterError("From date cannot be later than To date.");
      return;
    }

    setSubmittedFilters(draftFilters);
    setPagination((currentPagination) => ({
      ...currentPagination,
      currentPage: 1,
    }));
  };

  const handleClearFilters = () => {
    const nextFilters = createInitialFilters(selectedLogType);
    setDraftFilters(nextFilters);
    setSubmittedFilters(nextFilters);
    setFilterError("");
  };

  const handleChangeLogType = (logType) => {
    if (logType === selectedLogType) {
      return;
    }

    const nextFilters = createInitialFilters(logType);
    setSelectedLogType(logType);
    setDraftFilters(nextFilters);
    setSubmittedFilters(nextFilters);
    setFilterError("");
    setError("");
    setPageSize(10);
  };

  const handlePageChange = (nextPage) => {
    fetchLogs(nextPage, pageSize, submittedFilters, selectedLogType);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
  };

  const columns = logConfig.columns;

  return (
    <MasterShell pageClassName="logs-module" showMastersNav={false}>
      <div className="masters-card logs-module__layout new_logs-list">
        <div className="logs-module__tabs new_logs-tabs">
          {Object.entries(LOG_TYPE_CONFIG).map(([key, config]) => (
            <button
              key={key}
              className={`logs-module__tab new_logs-tab ${
                key === selectedLogType ? "logs-module__tab--active" : ""
              }`.trim()}
              onClick={() => handleChangeLogType(key)}
              type="button"
            >
              {config.label}
            </button>
          ))}
        </div>

        <form className="logs-module__filters new_logs-filters" onSubmit={handleApplyFilters}>
          {/* <div className="logs-module__filters-header">
            <div>
              <h2 className="logs-module__filters-title">Filters</h2>
              <p className="logs-module__filters-subtitle">
                Query params: {logConfig.requestParams.join(", ")}
              </p>
            </div>
          </div> */}

          {filterError ? <div className="logs-module__filters-error">{filterError}</div> : null}
          {usersError ? <div className="logs-module__filters-error">{usersError}</div> : null}

          <div
            className={`logs-module__filters-grid new_logs-filters-grid new_logs-filters-grid--${selectedLogType}`}
          >
            {effectiveFilterFields.map((field) => (
              <div key={field.name}>
                <label className="masters-form__label" htmlFor={field.name}>
                  {field.label}
                </label>

                {field.type === "select" ? (
                  <select
                    className="masters-form__select"
                    id={field.name}
                    name={field.name}
                    onChange={handleDraftChange}
                    value={draftFilters[field.name] || ""}
                  >
                    <option value="">All</option>
                    {(field.optionType === "users" ? userOptions : []).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="masters-form__control"
                    id={field.name}
                    name={field.name}
                    onChange={handleDraftChange}
                    placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                    type={field.type || "text"}
                    value={draftFilters[field.name] || ""}
                  />
                )}
              </div>
            ))}

            <div className="logs-module__filters-actions-cell new_logs-filters-actions-cell">
              <div className="logs-module__filters-actions new_logs-filters-actions">
                <button
                  className="masters-btn masters-btn--secondary"
                  onClick={handleClearFilters}
                  type="button"
                >
                  Reset Filters
                </button>
                <button className="masters-btn" type="submit">
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </form>

        {isLoading ? (
          <div className="masters-state">
            <div className="masters-state__loader" />
            <h2 className="masters-state__title">Loading logs</h2>
            <p className="masters-state__text">
              Please wait while the selected log stream is being fetched.
            </p>
          </div>
        ) : error ? (
          <div className="masters-state">
            <h2 className="masters-state__title">Unable to load logs</h2>
            <p className="masters-state__text">{error}</p>
            <div style={{ marginTop: "16px" }}>
              <button
                className="masters-btn"
                onClick={() =>
                  fetchLogs(
                    pagination.currentPage || 1,
                    pageSize,
                    submittedFilters,
                    selectedLogType
                  )
                }
                type="button"
              >
                Retry
              </button>
            </div>
          </div>
        ) : logRows.length === 0 ? (
          <div className="masters-state">
            <h2 className="masters-state__title">No logs found</h2>
            <p className="masters-state__text">{logConfig.emptyMessage}</p>
          </div>
        ) : (
          <>
            <div className="logs-module__table-wrap new_logs-table-wrap">
              <div className="logs-module__table-scroll new_logs-table-scroll">
                <table className="logs-module__table new_logs-table">
                  <thead>
                    <tr>
                      <th>Sr. No.</th>
                      {columns.map((column) => (
                        <th key={column.key}>{column.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {logRows.map((row, index) => {
                      const rowNumber =
                        (pagination.currentPage - 1) * pagination.pageSize + index + 1;

                      return (
                        <tr key={row.id || `${selectedLogType}-${rowNumber}`}>
                          <td>{rowNumber}</td>
                          {columns.map((column) => (
                            <td key={column.key}>
                              {column.render
                                ? column.render(row, { userLookup })
                                : row[column.key] || "-"}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="logs-module__footer new_logs-footer">
              <div className="logs-module__footer-note new_logs-footer-note">
                {getVisibleRange(pagination, logRows.length)}
              </div>
              <div className="logs-module__footer-pagination new_logs-footer-pagination">
                <select
                  className="masters-form__select masters-pagination__select"
                  id="page-size"
                  onChange={handlePageSizeChange}
                  value={pageSize}
                >
                  <option value={10}>10 / page</option>
                  <option value={25}>25 / page</option>
                  <option value={50}>50 / page</option>
                </select>
                <div className="logs-module__footer-pageinfo new_logs-footer-pageinfo">
                  Page {pagination.currentPage}
                  {pagination.totalPages ? ` of ${pagination.totalPages}` : ""}
                </div>
                <button
                  className="masters-btn masters-btn--secondary"
                  disabled={pagination.currentPage <= 1}
                  onClick={() => handlePageChange(Math.max(1, pagination.currentPage - 1))}
                  type="button"
                >
                  Previous
                </button>
                <button
                  className="masters-btn"
                  disabled={!pagination.hasNext}
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  type="button"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <MasterToast
        message={toast.message}
        onClose={() => setToast((currentToast) => ({ ...currentToast, open: false }))}
        open={toast.open}
        severity={toast.severity}
      />
    </MasterShell>
  );
}
